// Parse a thrust table pasted straight out of a datasheet, a spreadsheet or a
// web page into claimed-curve rows.
//
// Manufacturers publish this table in every layout imaginable, so rather than
// demand a fixed column order we read the HEADER ROW and map columns by
// keyword. A paste with no recognisable header is rejected with a message
// saying so — guessing the column order would silently file current as thrust.

// Order matters — the first column whose alias is a substring of the header
// cell wins. `thrust` MUST come before `throttle`, because throttle carries the
// loose "thr" alias and "thrust".includes("thr") is true: with throttle first,
// a "Thrust" header is claimed as the throttle column and the real throttle
// column is left unmapped. The reverse never happens, since "throttle" does not
// contain "thrust".
const COLUMNS = [
  { key: "thrust", aliases: ["thrust", "pull", "lift"] },
  { key: "throttle", aliases: ["throttle", "thr %", "thr"] },
  { key: "voltage", aliases: ["voltage", "volt", "v (v)"] },
  { key: "current", aliases: ["current", "amps", "amp", "i (a)"] },
  { key: "rpm", aliases: ["rpm", "speed"] },
  { key: "power", aliases: ["power", "watt"] },
  { key: "efficiency", aliases: ["efficiency", "efficency", "eff", "g/w"] },
  { key: "torque", aliases: ["torque"] },
  { key: "temp", aliases: ["temperature", "temp"] },
];

// The delimiter is decided once for the whole paste, not per line. Splitting on
// "any of tab/comma/pipe/gap" looks tolerant but is wrong: a tab-separated
// datasheet row containing "14,117" gets torn into two cells and every column
// after it shifts by one — thrust silently becomes power.
const pickDelimiter = (lines) => {
  if (lines.some((line) => line.includes("\t"))) return /\t/;
  if (lines.some((line) => line.includes("|"))) return /\s*\|\s*/;
  if (lines.some((line) => line.includes(","))) return /\s*,\s*/;
  return /\s{2,}/;
};

const splitCells = (line, delimiter) =>
  line
    .split(delimiter)
    .map((cell) => cell.trim())
    .filter((cell) => cell !== "");

const normalize = (s) => s.toLowerCase().replace(/[（(].*?[）)]/g, " ").replace(/[^a-z0-9%/ ]/g, " ").trim();

/** Map each column index to a known field, using the header row. */
const mapHeader = (cells) => {
  const mapping = {};
  const taken = new Set();
  cells.forEach((raw, index) => {
    const cell = normalize(raw);
    if (!cell) return;
    for (const { key, aliases } of COLUMNS) {
      if (taken.has(key)) continue;
      if (aliases.some((alias) => cell.includes(alias))) {
        mapping[index] = key;
        taken.add(key);
        return;
      }
    }
  });
  return mapping;
};

/** "14,117" → 14117; "45.5%" → 45.5; "—" → null. */
const toNumber = (raw) => {
  if (raw == null) return null;
  const cleaned = String(raw).replace(/[,%\s]/g, "").replace(/[^\d.+-eE]/g, "");
  if (cleaned === "" || cleaned === "-" || cleaned === ".") return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
};

/**
 * `{ rows, error }`. `rows` are objects keyed by the COLUMNS keys above; a row
 * without a throttle value is dropped, since throttle is what joins a claimed
 * curve to a measured one.
 */
export const parseCurve = (text) => {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    return { rows: [], error: "Paste at least a header row and one data row." };
  }

  const delimiter = pickDelimiter(lines);

  // The header is the first line that maps a Throttle column.
  let headerIndex = -1;
  let mapping = {};
  for (let i = 0; i < Math.min(lines.length, 5); i += 1) {
    const cells = splitCells(lines[i], delimiter);
    const candidate = mapHeader(cells);
    if (candidate && Object.values(candidate).includes("throttle")) {
      headerIndex = i;
      mapping = candidate;
      break;
    }
  }

  if (headerIndex === -1) {
    return {
      rows: [],
      error:
        "Couldn't find a header row with a Throttle column. Include column names — e.g. Throttle, Current, RPM, Thrust, Power, Efficiency, Torque.",
    };
  }

  const rows = [];
  for (let i = headerIndex + 1; i < lines.length; i += 1) {
    const cells = splitCells(lines[i], delimiter);
    const row = {};
    Object.entries(mapping).forEach(([index, key]) => {
      const value = toNumber(cells[Number(index)]);
      if (value !== null) row[key] = value;
    });
    // Throttle is the join key; a row without it cannot be compared to anything.
    if (row.throttle == null) continue;
    if (row.throttle < 0 || row.throttle > 100) continue;
    rows.push(row);
  }

  if (!rows.length) {
    return { rows: [], error: "No data rows found under the header." };
  }
  return { rows: rows.sort((a, b) => a.throttle - b.throttle), error: null };
};

/* eslint-disable no-console */
// Self-check: this parser decides which number is thrust and which is current,
// so a silent column shift would publish wrong data under a real brand's name.
if (process.env.NODE_ENV !== "production") {
  const tmotor = [
    "Throttle\tCurrent (A)\tRPM\tThrust (gf)\tPower (W)\tEfficiency (g/W)\tTorque (N·m)",
    "40%\t14.47\t1253\t14,117\t1331\t10.61\t8.36",
    "50%\t28.14\t1575\t22,744\t2590\t8.78\t13.43",
  ].join("\n");
  const { rows, error } = parseCurve(tmotor);
  console.assert(!error, `parseCurve: unexpected error ${error}`);
  console.assert(rows.length === 2, "parseCurve: expected 2 rows");
  console.assert(rows[0].throttle === 40 && rows[0].thrust === 14117, "parseCurve: thousands separator or throttle");
  console.assert(rows[0].current === 14.47 && rows[0].torque === 8.36, "parseCurve: column mapping shifted");
  console.assert(rows[1].efficiency === 8.78, "parseCurve: efficiency column");

  // Reordered columns must follow the header, not a fixed position.
  const reordered = parseCurve("Thrust,Throttle\n1000,25\n2000,50");
  console.assert(reordered.rows[0].thrust === 1000 && reordered.rows[0].throttle === 25, "parseCurve: header order");

  // "Thrust" must not be swallowed by throttle's loose "thr" alias.
  const both = parseCurve("Throttle,Thrust,Power\n50,22744,2590");
  console.assert(both.rows[0]?.throttle === 50 && both.rows[0]?.thrust === 22744, "parseCurve: thrust/throttle collision");

  // No header → refuse rather than guess.
  console.assert(parseCurve("40\t14.47\t1253").error, "parseCurve: headerless paste should error");
  console.assert(parseCurve("").error, "parseCurve: empty paste should error");
}
